/** Leave reminder + on-device, best-effort forecast-change alerts for saved trips (Release A). */

export type AlertSeverity = 'Extreme' | 'Severe' | 'Moderate' | 'Minor' | 'Unknown';

/** One sampled point along the route with its drive-time offset from departure. */
export type TripWatchPoint = {
  latitude: number;
  longitude: number;
  /** Seconds after departure that the car is expected to reach this point. */
  offsetSec: number;
  label?: string;
};

export type TripRecurrence =
  | { kind: 'once' }
  /** weekdays: 0 = Sunday … 6 = Saturday (JS Date#getDay). */
  | { kind: 'weekly'; weekdays: number[]; hour: number; minute: number };

export type TripAlertThresholds = {
  /** Notify when precipitation probability along the route reaches this % (0–100). */
  rainProbabilityPct: number;
  /** Notify when wind gusts along the route reach this (km/h; UI may show mph). */
  windGustKmh: number;
  /** Lowest NWS alert severity that triggers a notification. */
  alertMinSeverity: Exclude<AlertSeverity, 'Unknown'>;
  /** Also notify when a previously flagged risk clears. */
  notifyOnClearing: boolean;
};

export type TripAlertSummaryItem = { event: string; severity: AlertSeverity };

export type TripForecastSummary = {
  checkedAt: number;
  /** Departure instant the summary was computed for (ms). */
  departAt: number;
  maxPrecipPct: number | null;
  maxPrecipLabel?: string;
  maxPrecipEta?: number;
  maxGustKmh: number | null;
  maxGustLabel?: string;
  maxGustEta?: number;
  minTempC: number | null;
  maxTempC: number | null;
  alerts: TripAlertSummaryItem[];
  /** How many points had forecast data for their ETA. */
  pointsCovered: number;
  pointsTotal: number;
  /** Flags evaluated with hysteresis against the thresholds. */
  rainy: boolean;
  windy: boolean;
  alerted: boolean;
};

export type TripWatch = {
  id: string;
  name: string;
  /** Destination display name (for notification copy). */
  destinationName: string;
  savedRouteId?: string;
  points: TripWatchPoint[];
  durationSec: number;
  recurrence: TripRecurrence;
  /** Departure for one-off trips (ms since epoch). */
  departAt: number;
  reminderEnabled: boolean;
  /** Minutes before departure to fire the leave reminder. */
  reminderLeadMin: number;
  alertsEnabled: boolean;
  thresholds: TripAlertThresholds;
  lastSummary?: TripForecastSummary;
  lastNotifiedAt?: number;
  /** Fingerprint of the last change notification (dedupe). */
  lastNotifiedKey?: string;
  reminderNotificationIds?: string[];
  /** Show wind in mph in notification copy. */
  imperial?: boolean;
  createdAt: number;
  updatedAt: number;
};

export const DEFAULT_THRESHOLDS: TripAlertThresholds = {
  rainProbabilityPct: 50,
  windGustKmh: 56, // ≈ 35 mph
  alertMinSeverity: 'Moderate',
  notifyOnClearing: true,
};

export const REMINDER_LEAD_OPTIONS_MIN = [0, 15, 30, 60, 90] as const;
